import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';
import { ApiService } from '@shared/services/api/api.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ResearcherService {
    private endpoint = 'researcher';

    filterForm!: FormGroup;
    pageIndex: number = 0;
    pageSize: number = 10;
    sortActive: string = 'lastName';
    sortDirection: 'asc' | 'desc' = 'asc';
    totalCount: number = 0;

    constructor(
        private apiService: ApiService,
        private fb: FormBuilder
    ) {
        this.initializeFilterForm();
    }

    private initializeFilterForm(): void {
        this.filterForm = this.fb.group({
            firstName: [''],
            lastName: [''],
            role: [''],
            isActive: ['true']
        });
    }

    resetFilter(): void {
        this.filterForm.reset({
            firstName: '',
            lastName: '',
            role: '',
            isActive: 'true'
        });
        this.pageIndex = 0;
    }

    getAll(): Observable<Researcher[]> {
        const filters = this.filterForm.value;

        // Build query string
        let queryParams = '';

        // Add role filter if present
        if (filters.role) {
            queryParams = `role=${encodeURIComponent(filters.role)}`;
        }

        // Add sorting
        if (queryParams) queryParams += '&';
        queryParams += `sort=${this.sortActive}`;
        queryParams += `&order=${this.sortDirection}`;
        queryParams += `&pageIndex=${this.pageIndex + 1}`;
        queryParams += `&pageSize=${this.pageSize}`;

        // Add filters
        if (filters.firstName) {
            queryParams += `&firstName=${encodeURIComponent(filters.firstName)}`;
        }
        if (filters.lastName) {
            queryParams += `&lastName=${encodeURIComponent(filters.lastName)}`;
        }
        if (filters.isActive !== '' && filters.isActive !== null) {
            queryParams += `&isActive=${filters.isActive}`;
        }

        return this.apiService.get<any>(`${this.endpoint}?${queryParams}`)
            .pipe(
                map((response: any) => {
                    this.totalCount = response.totalCount;
                    return response.researchers;
                })
            );
    }

    getById(id: number): Observable<Researcher> {
        return this.apiService.getById<any>(this.endpoint, id);
    }

    create(researcher: Researcher, role?: string): Observable<Researcher> {
        const payload = {
            ...researcher,
            role: role || researcher.role || ''
        };
        return this.apiService.create<any>(this.endpoint, payload);
    }

    update(researcher: Researcher): Observable<Researcher> {
        return this.apiService.update<any>(this.endpoint, researcher);
    }

    delete(id: number): Observable<any> {
        return this.apiService.create<any>(`${this.endpoint}/${id}`, {});
    }

    downloadReport(): Observable<Blob> {
        const filters = this.filterForm.value;

        // Build query string
        let queryParams = '';

        // Add role filter if present
        if (filters.role) {
            queryParams = `role=${encodeURIComponent(filters.role)}`;
        }

        // Add filters
        if (filters.firstName) {
            queryParams += `&firstName=${encodeURIComponent(filters.firstName)}`;
        }
        if (filters.lastName) {
            queryParams += `&lastName=${encodeURIComponent(filters.lastName)}`;
        }
        if (filters.isActive !== '' && filters.isActive !== null) {
            queryParams += `&isActive=${filters.isActive}`;
        }

        return this.apiService.downloadExcelFile(`${this.endpoint}?${queryParams}&forExcel=true`);
    }

    searchResearchers(query: string, role?: string, isActive?: boolean): Observable<Researcher[]> {
        const q = (query ?? '').trim();
        if (!q) return of([]);

        const params: Record<string, string> = { q };
        if (role) params.role = role;
        if (isActive !== undefined) params.isActive = String(isActive);
        

        return this.apiService.getWithParam<Researcher[]>(
            `${this.endpoint}/ResearcherSearch`,
            { params }
        );
    }

}
